import { useForm, Controller } from 'react-hook-form';
import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AQF_LEVELS,
  AQF_LEVEL_LABELS,
  COURSE_CATEGORIES,
  DEFAULT_CATEGORY_LABELS,
  SUB_CATEGORIES,
  VISA_SUBCLASSES,
  type CourseCategory,
} from '@admitly/shared';
import { Button } from '../ui/Button.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { Select } from '../ui/Select.js';
import { Textarea } from '../ui/Textarea.js';
import {
  useAdminProviders,
} from '../../hooks/useAdminProviders.js';
import {
  useCreateCourse,
  useUnverifyCourse,
  useUpdateCourse,
  useVerifyCourse,
  type AdminCourse,
  type CreateCoursePayload,
} from '../../hooks/useAdminCourses.js';
import { formatDate } from '../../lib/format.js';
import {
  useCreatePrivateCourse,
  useUpdatePrivateCourse,
  type AdminPrivateCourse,
  type CreatePrivateCoursePayload,
} from '../../hooks/usePrivateCourses.js';

export type CourseFormVariant = 'master' | 'private';

/** Accepts either course shape; fields absent on one variant are simply undefined. */
type CourseFormInitial = Partial<AdminCourse & AdminPrivateCourse>;

type FormValues = {
  providerId: string;
  providerName: string;
  name: string;
  cricosCode: string;
  category: CourseCategory;
  subCategory: string;
  aqfLevel: string;
  durationText: string;
  durationMonths: string;
  campusLocations: string; // comma-separated
  intakeMonths: string; // comma-separated 1–12

  // Fees in AUD (we convert to cents on submit)
  annualTuitionAud: string;
  totalProgramAud: string;
  oshcAnnualAud: string;
  applicationFeeAud: string;
  materialFeesAud: string;

  ieltsOverall: string;
  ieltsMinBand: string;
  ieltsRequirementText: string;
  pteOverall: string;
  pteMinSkill: string;
  pteRequirementText: string;
  toeflOverall: string;
  toeflRequirementText: string;
  otherEnglishText: string;

  academicPrerequisites: string;
  courseStructure: string;
  careerOutcomesSummary: string;
  pathways: string;

  anzscoCodes: string; // comma-separated digits
  visaSubclassRelevance: string[];
  documentsRequired: string; // newline-separated
  scholarshipsAvailable: boolean;
  scholarshipsText: string;

  curationState: 'shell' | 'curated' | 'needs_review' | 'archived';
};

const CURATION_STATES = [
  { value: 'shell', label: 'Shell (imported, not yet reviewed)' },
  { value: 'curated', label: 'Curated (verified, live)' },
  { value: 'needs_review', label: 'Needs review' },
  { value: 'archived', label: 'Archived' },
];

function audToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const dollars = Number(trimmed.replace(/[,$\s]/g, ''));
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

function centsToAud(cents: number | null | undefined): string {
  if (cents == null) return '';
  return String(Math.round(cents / 100));
}

function toFormValues(c?: CourseFormInitial | null): FormValues {
  return {
    providerId: c?.providerId ?? '',
    providerName: c?.providerName ?? '',
    name: c?.name ?? '',
    cricosCode: c?.cricosCode ?? '',
    category: (c?.category as CourseCategory) ?? 'higher_education',
    subCategory: c?.subCategory ?? '',
    aqfLevel: c?.aqfLevel ?? '',
    durationText: c?.durationText ?? '',
    durationMonths: c?.durationMonths != null ? String(c.durationMonths) : '',
    campusLocations: (c?.campusLocations ?? []).join(', '),
    intakeMonths: (c?.intakeMonths ?? []).join(', '),

    annualTuitionAud: centsToAud(c?.annualTuitionCents),
    totalProgramAud: centsToAud(c?.totalProgramCents),
    oshcAnnualAud: centsToAud(c?.oshcAnnualCents),
    applicationFeeAud: centsToAud(c?.applicationFeeCents),
    materialFeesAud: centsToAud(c?.materialFeesCents),

    ieltsOverall: c?.ieltsOverall ?? '',
    ieltsMinBand: c?.ieltsMinBand ?? '',
    ieltsRequirementText: c?.ieltsRequirementText ?? '',
    pteOverall: c?.pteOverall != null ? String(c.pteOverall) : '',
    pteMinSkill: c?.pteMinSkill != null ? String(c.pteMinSkill) : '',
    pteRequirementText: c?.pteRequirementText ?? '',
    toeflOverall: c?.toeflOverall != null ? String(c.toeflOverall) : '',
    toeflRequirementText: c?.toeflRequirementText ?? '',
    otherEnglishText: c?.otherEnglishText ?? '',

    academicPrerequisites: c?.academicPrerequisites ?? '',
    courseStructure: c?.courseStructure ?? '',
    careerOutcomesSummary: c?.careerOutcomesSummary ?? '',
    pathways: c?.pathways ?? '',

    anzscoCodes: (c?.anzscoCodes ?? []).join(', '),
    visaSubclassRelevance: c?.visaSubclassRelevance ?? [],
    documentsRequired: (c?.documentsRequired ?? []).join('\n'),
    scholarshipsAvailable: !!c?.scholarshipsAvailable,
    scholarshipsText: c?.scholarshipsText ?? '',

    curationState: c?.curationState ?? 'curated',
  };
}

/** Fields shared by both master and private courses (provider + curation handled per-variant). */
function toBasePayload(v: FormValues) {
  return {
    name: v.name.trim(),
    cricosCode: v.cricosCode.trim() || null,
    category: v.category,
    subCategory: v.subCategory,
    aqfLevel: v.aqfLevel ? (v.aqfLevel as AdminCourse['aqfLevel']) : null,
    durationText: v.durationText.trim() || null,
    durationMonths: v.durationMonths ? Number(v.durationMonths) : null,
    campusLocations: v.campusLocations
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    intakeMonths: v.intakeMonths
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 12),

    annualTuitionCents: audToCents(v.annualTuitionAud),
    totalProgramCents: audToCents(v.totalProgramAud),
    oshcAnnualCents: audToCents(v.oshcAnnualAud),
    applicationFeeCents: audToCents(v.applicationFeeAud),
    materialFeesCents: audToCents(v.materialFeesAud),

    ieltsOverall: v.ieltsOverall.trim() || null,
    ieltsMinBand: v.ieltsMinBand.trim() || null,
    ieltsRequirementText: v.ieltsRequirementText.trim() || null,
    pteOverall: v.pteOverall ? Number(v.pteOverall) : null,
    pteMinSkill: v.pteMinSkill ? Number(v.pteMinSkill) : null,
    pteRequirementText: v.pteRequirementText.trim() || null,
    toeflOverall: v.toeflOverall ? Number(v.toeflOverall) : null,
    toeflRequirementText: v.toeflRequirementText.trim() || null,
    otherEnglishText: v.otherEnglishText.trim() || null,

    academicPrerequisites: v.academicPrerequisites.trim() || null,
    courseStructure: v.courseStructure.trim() || null,
    careerOutcomesSummary: v.careerOutcomesSummary.trim() || null,
    pathways: v.pathways.trim() || null,

    anzscoCodes: v.anzscoCodes
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    visaSubclassRelevance: v.visaSubclassRelevance,
    documentsRequired: v.documentsRequired
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean),
    scholarshipsAvailable: v.scholarshipsAvailable,
    scholarshipsText: v.scholarshipsText.trim() || null,
  };
}

function toMasterPayload(v: FormValues): CreateCoursePayload {
  return { ...toBasePayload(v), providerId: v.providerId, curationState: v.curationState };
}

function toPrivatePayload(v: FormValues): CreatePrivateCoursePayload {
  return { ...toBasePayload(v), providerName: v.providerName.trim() };
}

type Props = {
  initial?: CourseFormInitial | null;
  /** 'private' renders a free-text provider field, hides curation, and targets the private API. */
  variant?: CourseFormVariant;
};

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-5 pt-8 first:pt-0 border-t first:border-t-0 border-line">
    <div>
      <h2 className="font-serif text-lg text-ink">{title}</h2>
      {description && <p className="text-sm text-muted mt-1">{description}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

export function CourseForm({ initial, variant = 'master' }: Props) {
  const navigate = useNavigate();
  const isEdit = !!initial;
  const isPrivate = variant === 'private';
  const listPath = isPrivate ? '/app/admin/private-courses' : '/app/admin/courses';

  // Master providers feed the dropdown; skip the (super-admin-only) fetch for private courses.
  const providersQ = useAdminProviders(undefined, { enabled: !isPrivate });
  const createMaster = useCreateCourse();
  const updateMaster = useUpdateCourse(initial?.id ?? '');
  const createPrivate = useCreatePrivateCourse();
  const updatePrivate = useUpdatePrivateCourse(initial?.id ?? '');
  const verify = useVerifyCourse(initial?.id ?? '');
  const unverify = useUnverifyCourse(initial?.id ?? '');

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: toFormValues(initial) });

  const category = watch('category');
  const subCategoryOptions = useMemo(
    () => SUB_CATEGORIES[category as CourseCategory] ?? [],
    [category],
  );

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isPrivate) {
        const payload = toPrivatePayload(values);
        if (isEdit) await updatePrivate.mutateAsync(payload);
        else await createPrivate.mutateAsync(payload);
      } else {
        const payload = toMasterPayload(values);
        if (isEdit) await updateMaster.mutateAsync(payload);
        else await createMaster.mutateAsync(payload);
      }
      navigate({ to: listPath });
    } catch (e) {
      console.error(e);
    }
  });

  const create = isPrivate ? createPrivate : createMaster;
  const update = isPrivate ? updatePrivate : updateMaster;
  const mutationError =
    (create.error as Error | null)?.message ?? (update.error as Error | null)?.message ?? null;

  const providers = providersQ.data?.items ?? [];

  return (
    <form onSubmit={onSubmit} className="space-y-8 max-w-4xl pb-12">
      {mutationError && (
        <div className="bg-danger-bg border border-danger/30 text-danger rounded-lg px-4 py-3 text-sm">
          {mutationError}
        </div>
      )}

      <Section title="Basics" description="The fundamentals that show on the course card.">
        {isPrivate ? (
          <div>
            <Label htmlFor="providerName">Provider</Label>
            <Input
              id="providerName"
              placeholder="e.g. Wollongong College of Business"
              invalid={!!errors.providerName}
              {...register('providerName', { required: 'Provider is required' })}
            />
            {errors.providerName && (
              <p className="text-danger text-xs mt-1">{errors.providerName.message}</p>
            )}
          </div>
        ) : (
          <div>
            <Label htmlFor="providerId">Provider</Label>
            <Select
              id="providerId"
              invalid={!!errors.providerId}
              {...register('providerId', { required: 'Provider is required' })}
            >
              <option value="">— Select provider —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.shortName ? ` (${p.shortName})` : ''}
                </option>
              ))}
            </Select>
            {errors.providerId && (
              <p className="text-danger text-xs mt-1">{errors.providerId.message}</p>
            )}
          </div>
        )}

        <div>
          <Label htmlFor="name">Course name</Label>
          <Input
            id="name"
            placeholder="e.g. Master of Information Technology"
            invalid={!!errors.name}
            {...register('name', { required: 'Course name is required' })}
          />
          {errors.name && <p className="text-danger text-xs mt-1">{errors.name.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="cricosCode">CRICOS code</Label>
            <Input
              id="cricosCode"
              placeholder="084185G"
              invalid={!!errors.cricosCode}
              {...register('cricosCode', {
                pattern: {
                  value: /^$|^\d{6}[A-Z]$/,
                  message: 'Format: 6 digits + uppercase letter',
                },
              })}
            />
            {errors.cricosCode && (
              <p className="text-danger text-xs mt-1">{errors.cricosCode.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="aqfLevel">AQF level</Label>
            <Select id="aqfLevel" {...register('aqfLevel')}>
              <option value="">—</option>
              {AQF_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {AQF_LEVEL_LABELS[l]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="category">Category</Label>
            <Select
              id="category"
              invalid={!!errors.category}
              {...register('category', { required: true })}
            >
              {COURSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {DEFAULT_CATEGORY_LABELS[c]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="subCategory">Sub-category</Label>
            <Select
              id="subCategory"
              invalid={!!errors.subCategory}
              {...register('subCategory', { required: 'Sub-category is required' })}
            >
              <option value="">— Select —</option>
              {subCategoryOptions.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.label}
                </option>
              ))}
            </Select>
            {errors.subCategory && (
              <p className="text-danger text-xs mt-1">{errors.subCategory.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="durationText">Duration (display)</Label>
            <Input
              id="durationText"
              placeholder="2 years full-time"
              {...register('durationText')}
            />
          </div>
          <div>
            <Label htmlFor="durationMonths">Duration (months)</Label>
            <Input
              id="durationMonths"
              type="number"
              min={1}
              max={120}
              placeholder="24"
              {...register('durationMonths')}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="campusLocations">Campus locations</Label>
            <Input
              id="campusLocations"
              placeholder="Wollongong, Sydney CBD"
              {...register('campusLocations')}
            />
            <p className="text-xs text-muted-soft mt-1">Comma-separated</p>
          </div>
          <div>
            <Label htmlFor="intakeMonths">Intake months</Label>
            <Input
              id="intakeMonths"
              placeholder="2, 7"
              {...register('intakeMonths')}
            />
            <p className="text-xs text-muted-soft mt-1">Numbers 1–12, comma-separated</p>
          </div>
        </div>
      </Section>

      <Section title="Fees" description="All amounts in AUD. We store in cents internally.">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="annualTuitionAud">Annual tuition</Label>
            <Input
              id="annualTuitionAud"
              type="number"
              min={0}
              placeholder="42816"
              {...register('annualTuitionAud')}
            />
          </div>
          <div>
            <Label htmlFor="totalProgramAud">Total program</Label>
            <Input
              id="totalProgramAud"
              type="number"
              min={0}
              placeholder="85632"
              {...register('totalProgramAud')}
            />
          </div>
          <div>
            <Label htmlFor="oshcAnnualAud">OSHC (annual)</Label>
            <Input
              id="oshcAnnualAud"
              type="number"
              min={0}
              placeholder="650"
              {...register('oshcAnnualAud')}
            />
          </div>
          <div>
            <Label htmlFor="applicationFeeAud">Application fee</Label>
            <Input
              id="applicationFeeAud"
              type="number"
              min={0}
              placeholder="100"
              {...register('applicationFeeAud')}
            />
          </div>
          <div>
            <Label htmlFor="materialFeesAud">Material fees (annual)</Label>
            <Input
              id="materialFeesAud"
              type="number"
              min={0}
              placeholder="0"
              {...register('materialFeesAud')}
            />
          </div>
        </div>
      </Section>

      <Section title="English requirements">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="ieltsOverall">IELTS overall</Label>
            <Input
              id="ieltsOverall"
              placeholder="6.5"
              {...register('ieltsOverall', {
                pattern: { value: /^$|^\d+(\.\d{1,2})?$/, message: 'Number like 6.5' },
              })}
              invalid={!!errors.ieltsOverall}
            />
            {errors.ieltsOverall && (
              <p className="text-danger text-xs mt-1">{errors.ieltsOverall.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="ieltsMinBand">IELTS min band</Label>
            <Input
              id="ieltsMinBand"
              placeholder="6.0"
              {...register('ieltsMinBand', {
                pattern: { value: /^$|^\d+(\.\d{1,2})?$/, message: 'Number like 6.0' },
              })}
              invalid={!!errors.ieltsMinBand}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="ieltsRequirementText">IELTS requirement (free text)</Label>
          <Textarea
            id="ieltsRequirementText"
            rows={2}
            placeholder="IELTS Academic 6.5 overall with no band below 6.0"
            {...register('ieltsRequirementText')}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="pteOverall">PTE overall</Label>
            <Input
              id="pteOverall"
              type="number"
              min={0}
              max={90}
              placeholder="64"
              {...register('pteOverall')}
            />
          </div>
          <div>
            <Label htmlFor="pteMinSkill">PTE min skill</Label>
            <Input
              id="pteMinSkill"
              type="number"
              min={0}
              max={90}
              placeholder="54"
              {...register('pteMinSkill')}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="pteRequirementText">PTE requirement (free text)</Label>
          <Textarea
            id="pteRequirementText"
            rows={2}
            {...register('pteRequirementText')}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="toeflOverall">TOEFL iBT</Label>
            <Input
              id="toeflOverall"
              type="number"
              min={0}
              max={120}
              placeholder="85"
              {...register('toeflOverall')}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="toeflRequirementText">TOEFL requirement (free text)</Label>
          <Textarea
            id="toeflRequirementText"
            rows={2}
            {...register('toeflRequirementText')}
          />
        </div>

        <div>
          <Label htmlFor="otherEnglishText">Other accepted English tests</Label>
          <Textarea
            id="otherEnglishText"
            rows={2}
            placeholder="Cambridge Advanced 176; Duolingo 115; OET B; …"
            {...register('otherEnglishText')}
          />
        </div>
      </Section>

      <Section title="Content">
        <div>
          <Label htmlFor="academicPrerequisites">Academic prerequisites</Label>
          <Textarea
            id="academicPrerequisites"
            rows={3}
            placeholder="Bachelor degree in a relevant discipline with credit average."
            {...register('academicPrerequisites')}
          />
        </div>
        <div>
          <Label htmlFor="courseStructure">Course structure</Label>
          <Textarea
            id="courseStructure"
            rows={5}
            {...register('courseStructure')}
          />
        </div>
        <div>
          <Label htmlFor="careerOutcomesSummary">Career outcomes</Label>
          <Textarea
            id="careerOutcomesSummary"
            rows={3}
            {...register('careerOutcomesSummary')}
          />
        </div>
        <div>
          <Label htmlFor="pathways">Pathways</Label>
          <Textarea id="pathways" rows={3} {...register('pathways')} />
        </div>
      </Section>

      <Section title="Migration" description="How this course maps to skilled-migration outcomes.">
        <div>
          <Label htmlFor="anzscoCodes">ANZSCO codes</Label>
          <Input
            id="anzscoCodes"
            placeholder="261313, 261111"
            {...register('anzscoCodes')}
          />
          <p className="text-xs text-muted-soft mt-1">Comma-separated 4–6 digit codes</p>
        </div>

        <div>
          <Label>Visa subclass relevance</Label>
          <Controller
            control={control}
            name="visaSubclassRelevance"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2 mt-1">
                {VISA_SUBCLASSES.map((v) => {
                  const checked = field.value.includes(v.subclass);
                  return (
                    <Checkbox
                      key={v.subclass}
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          field.onChange([...field.value, v.subclass]);
                        } else {
                          field.onChange(field.value.filter((x) => x !== v.subclass));
                        }
                      }}
                      label={
                        <span>
                          <span className="font-medium">Subclass {v.subclass}</span>
                          <span className="text-muted ml-1.5">· {v.name}</span>
                        </span>
                      }
                    />
                  );
                })}
              </div>
            )}
          />
        </div>
      </Section>

      <Section title="Documents & extras">
        <div>
          <Label htmlFor="documentsRequired">Documents required to apply</Label>
          <Textarea
            id="documentsRequired"
            rows={6}
            placeholder={`Passport bio-data page\nAcademic transcripts\nEnglish test score report\nCV / resume`}
            {...register('documentsRequired')}
          />
          <p className="text-xs text-muted-soft mt-1">One per line</p>
        </div>

        <div>
          <Controller
            control={control}
            name="scholarshipsAvailable"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                label="Scholarships available"
              />
            )}
          />
        </div>
        <div>
          <Label htmlFor="scholarshipsText">Scholarships notes</Label>
          <Textarea
            id="scholarshipsText"
            rows={3}
            {...register('scholarshipsText')}
          />
        </div>
      </Section>

      {!isPrivate && (
        <Section title="Curation">
          <div>
            <Label htmlFor="curationState">State</Label>
            <Select id="curationState" {...register('curationState')}>
              {CURATION_STATES.map((cs) => (
                <option key={cs.value} value={cs.value}>
                  {cs.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-muted mt-1.5">
              "Curated" controls whether consultants can see this course. It does
              not grant the Verified badge — that comes from the panel below.
            </p>
          </div>
        </Section>
      )}

      {!isPrivate && isEdit && (
        <Section
          title="Verification"
          description="The green Verified badge means a person checked this course. Saving a change to any course fact clears it — re-verify after checking again."
        >
          {initial?.verifiedAt ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="badge bg-success-bg text-success">
                Verified by {initial.verifiedByName ?? 'a former team member'} on{' '}
                {formatDate(initial.verifiedAt)}
              </span>
              <Button
                type="button"
                variant="ghost"
                loading={unverify.isPending}
                onClick={() => unverify.mutate()}
              >
                Remove verification
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-muted">Not verified</span>
              <Button
                type="button"
                loading={verify.isPending}
                onClick={() => verify.mutate()}
              >
                Mark as verified
              </Button>
            </div>
          )}
          {(verify.error ?? unverify.error) && (
            <p className="text-danger text-xs">
              {((verify.error ?? unverify.error) as Error).message}
            </p>
          )}
        </Section>
      )}

      <div className="flex items-center gap-3 pt-6 border-t border-line">
        <Button type="submit" loading={isSubmitting}>
          {isEdit ? 'Save changes' : 'Create course'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => navigate({ to: listPath })}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
